/**
 * De prijsregels van een woning, en wat ze doen.
 *
 * Het scherm beantwoordt drie vragen in deze volgorde, en die volgorde is de hele
 * opzet:
 *
 * 1. **Wat kost een nacht straks?** De kalender staat bovenaan. Een verhuurder die
 *    hier komt, wil eerst zien wat er uit zijn regels komt — niet een formulier.
 * 2. **Wat zou het worden?** De simulatie ernaast, als hij regels in voorbereiding
 *    heeft. Dat is de eis uit hoofdstuk 19: eerst zien wat het doet, dan aanzetten.
 * 3. **Waarom?** De regels zelf, met per regel wat hij bijdraagt.
 *
 * Wat dit scherm uitdrukkelijk toont en wat elders vaak ontbreekt: **per nacht welke
 * regels eraan hebben bijgedragen.** Een prijs die uit een stapel regels komt en die
 * niemand kan navertellen, is een prijs die een verhuurder niet durft aan te zetten.
 */
import { useCallback, useEffect, useState } from 'react'
import type { FormEvent, ReactElement } from 'react'
import { useFormatters } from '@siteshowroom/i18n/react'

import { useTranslate } from '../lib/i18n-provider'
import { listResidences } from '../lib/media-client'
import type { ResidenceOption } from '../lib/media-client'
import { monthStart } from '../lib/overview-client'
import {
  RULE_KINDS,
  createEvent,
  createRule,
  fetchCalendar,
  fetchSimulation,
  formatAdjustment,
  listEvents,
  listRules,
  pricingErrorMessageKey,
  retireEvent,
  retireRule,
  saveBounds,
  updateRule,
} from '../lib/pricing-client'
import type {
  NightPrice,
  PricingEvent,
  PricingFailure,
  PricingResult,
  PricingRule,
  RateCalendar,
  RuleInput,
  RuleKind,
  Simulation,
} from '../lib/pricing-client'

function FailureNotice({ error }: { readonly error: PricingFailure }): ReactElement {
  const t = useTranslate()
  return (
    <div className="notice notice--error" role="alert">
      <p className="notice__title">{t(pricingErrorMessageKey(error))}</p>
      <code className="notice__detail">{error.message}</code>
    </div>
  )
}

export function PricingPage(): ReactElement {
  const t = useTranslate()
  const formatters = useFormatters()

  const [residences, setResidences] = useState<readonly ResidenceOption[]>([])
  const [residenceId, setResidenceId] = useState<string | null>(null)
  const [monthOffset, setMonthOffset] = useState(0)
  const [calendar, setCalendar] = useState<RateCalendar | null>(null)
  const [simulation, setSimulation] = useState<Simulation | null>(null)
  const [rules, setRules] = useState<readonly PricingRule[]>([])
  const [events, setEvents] = useState<readonly PricingEvent[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<PricingFailure | null>(null)

  const today = new Date()
  const from = monthStart(today, monthOffset)
  // De kalender is inclusief aan beide kanten: het gaat om nachten (§17.32). De
  // laatste nacht van de maand is dus de dag vóór de eerste van de volgende.
  const to = shift(monthStart(today, monthOffset + 1), -1)

  useEffect(() => {
    let cancelled = false
    void listResidences().then((result) => {
      if (cancelled || !result.ok) {
        return
      }
      setResidences(result.data)
      const first = result.data[0]
      setResidenceId(first === undefined ? null : first.id)
    })
    return () => {
      cancelled = true
    }
  }, [])

  const load = useCallback(async (): Promise<void> => {
    if (residenceId === null) {
      return
    }
    const [cal, sim, rls, evs] = await Promise.all([
      fetchCalendar(residenceId, { from, to }),
      fetchSimulation(residenceId, { from, to }),
      listRules(residenceId),
      listEvents(residenceId),
    ])
    if (cal.ok) {
      setCalendar(cal.data)
    } else {
      setError(cal.error)
    }
    if (sim.ok) {
      setSimulation(sim.data)
    }
    if (rls.ok) {
      setRules(rls.data)
    }
    if (evs.ok) {
      setEvents(evs.data)
    }
  }, [residenceId, from, to])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setError(null)
    void load()
  }, [load])

  const run = useCallback(
    async (work: () => Promise<PricingResult<unknown>>): Promise<void> => {
      setBusy(true)
      setError(null)
      const result = await work()
      if (!result.ok) {
        setError(result.error)
      }
      await load()
      setBusy(false)
    },
    [load],
  )

  const drafts = rules.filter((rule) => rule.status === 'draft').length

  return (
    <section className="page" aria-labelledby="pricing-heading">
      <div className="page__header">
        <h1 className="page__title" id="pricing-heading">
          {t('dashboard.pricing.title')}
        </h1>
        <button
          className="button"
          disabled={residenceId === null || busy}
          onClick={() => {
            void load()
          }}
          type="button"
        >
          {t('common.action.reload')}
        </button>
      </div>

      <p className="prose">{t('dashboard.pricing.intro')}</p>

      {residences.length > 1 && (
        <label className="field field--inline">
          <span className="field__label">{t('dashboard.media.residence')}</span>
          <select
            className="field__input"
            disabled={busy}
            onChange={(event) => {
              setResidenceId(event.target.value)
            }}
            value={residenceId ?? ''}
          >
            {residences.map((residence) => (
              <option key={residence.id} value={residence.id}>
                {residence.name}
              </option>
            ))}
          </select>
        </label>
      )}

      {error !== null && <FailureNotice error={error} />}

      <div className="period">
        <button
          className="button button--quiet"
          onClick={() => {
            setMonthOffset((current) => current - 1)
          }}
          type="button"
        >
          {t('dashboard.overview.period.previous')}
        </button>
        <span className="period__label">
          {formatters.formatDate(`${from}T00:00:00Z`, {
            timeZone: 'UTC',
            year: 'numeric',
            month: 'long',
          })}
        </span>
        <button
          className="button button--quiet"
          onClick={() => {
            setMonthOffset((current) => current + 1)
          }}
          type="button"
        >
          {t('dashboard.overview.period.next')}
        </button>
      </div>

      {calendar !== null && (
        <RateGrid
          calendar={calendar}
          from={from}
          proposed={drafts > 0 ? (simulation?.proposed ?? null) : null}
        />
      )}

      {/* Alleen wanneer er iets te simuleren valt. Een verschil van nul tonen bij
          een verhuurder zonder conceptregels is een vraag beantwoorden die hij niet
          heeft gesteld. */}
      {drafts > 0 && simulation !== null && (
        <div className="notice notice--action" role="status">
          <p className="notice__title">
            {t('dashboard.pricing.simulation.summary', {
              count: drafts,
              current: formatters.formatMoney(simulation.currentTotalCents, simulation.currency),
              proposed: formatters.formatMoney(simulation.proposedTotalCents, simulation.currency),
              difference: formatters.formatMoney(simulation.differenceCents, simulation.currency),
            })}
          </p>
        </div>
      )}

      {residenceId !== null && (
        <>
          <BoundsForm
            busy={busy}
            currency={calendar?.currency ?? 'EUR'}
            onSave={(min, max) => {
              void run(() => saveBounds(residenceId, min, max))
            }}
          />

          <RuleList
            busy={busy}
            events={events}
            onRetire={(ruleId) => {
              void run(() => retireRule(residenceId, ruleId))
            }}
            onSave={(ruleId, input) => {
              void run(() =>
                ruleId === null
                  ? createRule(residenceId, input)
                  : updateRule(residenceId, ruleId, input),
              )
            }}
            rules={rules}
          />

          <EventList
            busy={busy}
            events={events}
            onCreate={(input) => {
              void run(() => createEvent(residenceId, input))
            }}
            onRetire={(eventId) => {
              void run(() => retireEvent(residenceId, eventId))
            }}
          />
        </>
      )}
    </section>
  )
}

/**
 * Het maandraster met de prijs per nacht.
 *
 * Elke dag toont het bedrag, en waar een conceptregel iets zou veranderen ook wat het
 * zou worden. De bijdragen staan in de tooltip: welke regels deze prijs hebben
 * gemaakt, met hun aandeel.
 */
function RateGrid({
  calendar,
  from,
  proposed,
}: {
  readonly calendar: RateCalendar
  readonly from: string
  readonly proposed: readonly NightPrice[] | null
}): ReactElement {
  const t = useTranslate()
  const formatters = useFormatters()

  const proposedByDate = new Map((proposed ?? []).map((night) => [night.date, night]))
  const first = new Date(`${from}T00:00:00Z`)
  const leading = (first.getUTCDay() + 6) % 7

  return (
    <div className="rate-grid">
      {Array.from({ length: leading }, (_unused, index) => (
        <span aria-hidden="true" className="rate-grid__pad" key={`pad-${String(index)}`} />
      ))}
      {calendar.nights.map((night) => {
        const other = proposedByDate.get(night.date)
        const changes = other !== undefined && other.rateCents !== night.rateCents
        return (
          <span
            className="rate-cell"
            data-clamped={night.clamped ? 'true' : undefined}
            key={night.date}
            title={explain(night, t)}
          >
            <span className="rate-cell__day">{Number(night.date.slice(8, 10))}</span>
            <span className="rate-cell__amount">
              {formatters.formatMoney(night.rateCents, calendar.currency)}
            </span>
            {changes && (
              <span className="rate-cell__proposed">
                {formatters.formatMoney(other.rateCents, calendar.currency)}
              </span>
            )}
          </span>
        )
      })}
    </div>
  )
}

/** Welke regels deze prijs hebben gemaakt. Leeg wanneer het het basistarief is. */
function explain(
  night: NightPrice,
  t: (key: string, values?: Record<string, never>) => string,
): string {
  if (night.contributions.length === 0) {
    return t('dashboard.pricing.calendar.base')
  }
  return night.contributions
    .map(
      (entry) =>
        `${entry.label !== '' ? entry.label : entry.kind} ${formatAdjustment(entry.adjustPermille)}`,
    )
    .join(' · ')
}

function BoundsForm({
  busy,
  currency,
  onSave,
}: {
  readonly busy: boolean
  readonly currency: string
  readonly onSave: (min: number | null, max: number | null) => void
}): ReactElement {
  const t = useTranslate()
  const [min, setMin] = useState('')
  const [max, setMax] = useState('')

  function submit(event: FormEvent): void {
    event.preventDefault()
    onSave(toCents(min), toCents(max))
  }

  return (
    <form className="bounds-form" onSubmit={submit}>
      <h2 className="page__sectionTitle">{t('dashboard.pricing.bounds.title')}</h2>
      {/* Zonder bodem staat een stapel kortingen een prijs van bijna niets toe. Dat
          hoort er te staan, want leeg laten voelt als "geen mening". */}
      <p className="prose">{t('dashboard.pricing.bounds.intro')}</p>

      <div className="bounds-form__row">
        <label className="field">
          <span className="field__label">{t('dashboard.pricing.bounds.floor', { currency })}</span>
          <input
            className="field__input field__input--narrow"
            disabled={busy}
            min={0}
            onChange={(event) => {
              setMin(event.target.value)
            }}
            step="0.01"
            type="number"
            value={min}
          />
        </label>
        <label className="field">
          <span className="field__label">
            {t('dashboard.pricing.bounds.ceiling', { currency })}
          </span>
          <input
            className="field__input field__input--narrow"
            disabled={busy}
            min={0}
            onChange={(event) => {
              setMax(event.target.value)
            }}
            step="0.01"
            type="number"
            value={max}
          />
        </label>
        <button className="button" disabled={busy} type="submit">
          {t('dashboard.pricing.bounds.save')}
        </button>
      </div>
    </form>
  )
}

/** Lege invoer betekent uitdrukkelijk "geen grens" en niet "nul". */
function toCents(value: string): number | null {
  if (value.trim() === '') {
    return null
  }
  const amount = Number(value)
  return Number.isFinite(amount) ? Math.round(amount * 100) : null
}

function RuleList({
  rules,
  events,
  busy,
  onSave,
  onRetire,
}: {
  readonly rules: readonly PricingRule[]
  readonly events: readonly PricingEvent[]
  readonly busy: boolean
  readonly onSave: (ruleId: string | null, input: RuleInput) => void
  readonly onRetire: (ruleId: string) => void
}): ReactElement {
  const t = useTranslate()
  // `null` = niets open, '' = een nieuwe regel, anders het id van de regel die
  // bewerkt wordt. Eén toestand en geen twee, want "nieuw" en "bewerken" tegelijk
  // open is een scherm met twee formulieren die elkaar overschrijven.
  const [editing, setEditing] = useState<string | null>(null)

  return (
    <>
      <h2 className="page__sectionTitle">{t('dashboard.pricing.rules.title')}</h2>

      {rules.length === 0 ? (
        <p className="page__status" role="status">
          {t('dashboard.pricing.rules.empty')}
        </p>
      ) : (
        <ul className="rule-list">
          {rules.map((rule) => (
            <li className="rule" data-status={rule.status} key={rule.id}>
              <p className="rule__head">
                <span className="rule__adjust">{formatAdjustment(rule.adjustPermille)}</span>
                <span className="rule__label">
                  {rule.label !== '' ? rule.label : t(`dashboard.pricing.kind.${rule.kind}`)}
                </span>
                <span className="rule__status">{t(`dashboard.pricing.status.${rule.status}`)}</span>
              </p>
              <p className="rule__detail">{describeRule(rule, t)}</p>
              {editing === rule.id && (
                <RuleForm
                  busy={busy}
                  events={events}
                  initial={rule}
                  key={rule.id}
                  onCancel={() => {
                    setEditing(null)
                  }}
                  onSave={(input) => {
                    onSave(rule.id, input)
                    setEditing(null)
                  }}
                />
              )}
              <div className="rule__actions">
                {/* Bewerken ontbrak: een typefout in een percentage betekende de
                    regel intrekken en opnieuw maken, en dan is de historie weg. */}
                <button
                  className="button button--quiet"
                  disabled={busy}
                  onClick={() => {
                    setEditing(editing === rule.id ? null : rule.id)
                  }}
                  type="button"
                >
                  {editing === rule.id
                    ? t('dashboard.pricing.rules.cancel')
                    : t('dashboard.pricing.rules.edit')}
                </button>
                <button
                  className="button button--quiet"
                  disabled={busy}
                  onClick={() => {
                    // Aanzetten of terugzetten naar concept: één knop, want dat is
                    // de enige overgang die een verhuurder hier maakt.
                    onSave(rule.id, {
                      ...toInput(rule),
                      status: rule.status === 'active' ? 'draft' : 'active',
                    })
                  }}
                  type="button"
                >
                  {rule.status === 'active'
                    ? t('dashboard.pricing.rules.deactivate')
                    : t('dashboard.pricing.rules.activate')}
                </button>
                <button
                  className="button button--danger"
                  disabled={busy}
                  onClick={() => {
                    if (window.confirm(t('dashboard.pricing.rules.confirmRetire'))) {
                      onRetire(rule.id)
                    }
                  }}
                  type="button"
                >
                  {t('dashboard.pricing.rules.retire')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing === '' ? (
        <RuleForm
          busy={busy}
          events={events}
          initial={null}
          onCancel={() => {
            setEditing(null)
          }}
          onSave={(input) => {
            onSave(null, input)
            setEditing(null)
          }}
        />
      ) : (
        <button
          className="button button--primary"
          disabled={busy}
          onClick={() => {
            setEditing('')
          }}
          type="button"
        >
          {t('dashboard.pricing.rules.add')}
        </button>
      )}
    </>
  )
}

function toInput(rule: PricingRule): RuleInput {
  return {
    kind: rule.kind,
    label: rule.label,
    status: rule.status,
    adjustPermille: rule.adjustPermille,
    priority: rule.priority,
    startsOn: rule.startsOn,
    endsOn: rule.endsOn,
    weekdays: rule.weekdays,
    eventId: rule.eventId,
    occupancyBelowPermille: rule.occupancyBelowPermille,
    occupancyAbovePermille: rule.occupancyAbovePermille,
    leadTimeWithinDays: rule.leadTimeWithinDays,
    leadTimeBeyondDays: rule.leadTimeBeyondDays,
  }
}

/** Wat een regel doet, in één regel tekst. */
function describeRule(
  rule: PricingRule,
  t: (key: string, values?: Record<string, never>) => string,
): string {
  switch (rule.kind) {
    case 'season':
      return `${rule.startsOn ?? ''} – ${rule.endsOn ?? ''}`
    case 'weekday':
      return (rule.weekdays ?? [])
        .map((day) => t(`dashboard.pricing.weekday.${String(day)}`))
        .join(', ')
    case 'event':
      return rule.eventName
    case 'occupancy':
      return [
        rule.occupancyAbovePermille === null
          ? null
          : `≥ ${String(rule.occupancyAbovePermille / 10)}%`,
        rule.occupancyBelowPermille === null
          ? null
          : `≤ ${String(rule.occupancyBelowPermille / 10)}%`,
      ]
        .filter((part): part is string => part !== null)
        .join(' · ')
    case 'lead_time':
      return [
        rule.leadTimeBeyondDays === null ? null : `≥ ${String(rule.leadTimeBeyondDays)}d`,
        rule.leadTimeWithinDays === null ? null : `≤ ${String(rule.leadTimeWithinDays)}d`,
      ]
        .filter((part): part is string => part !== null)
        .join(' · ')
  }
}

/**
 * Het formulier voor één regel, nieuw of bestaand.
 *
 * `initial` is `null` voor een nieuwe regel. Eén formulier voor allebei, want de
 * velden zijn dezelfde en twee varianten lopen gegarandeerd uit elkaar.
 *
 * Een leeg getalveld betekent hier "geen grens" en niet "nul". Dat onderscheid is
 * dragend: een bezettingsregel met een ondergrens van nul geldt altijd, een regel
 * zonder ondergrens alleen onder de bovengrens.
 */
function RuleForm({
  events,
  busy,
  initial,
  onSave,
  onCancel,
}: {
  readonly events: readonly PricingEvent[]
  readonly busy: boolean
  readonly initial: PricingRule | null
  readonly onSave: (input: RuleInput) => void
  readonly onCancel: () => void
}): ReactElement {
  const t = useTranslate()

  const [kind, setKind] = useState<RuleKind>(initial?.kind ?? 'season')
  const [label, setLabel] = useState(initial?.label ?? '')
  const [percent, setPercent] = useState(
    initial === null ? '20' : String(initial.adjustPermille / 10),
  )
  const [priority, setPriority] = useState(String(initial?.priority ?? 0))
  const [startsOn, setStartsOn] = useState(initial?.startsOn ?? '')
  const [endsOn, setEndsOn] = useState(initial?.endsOn ?? '')
  const [weekdays, setWeekdays] = useState<readonly number[]>(initial?.weekdays ?? [5, 6])
  const [eventId, setEventId] = useState(initial?.eventId ?? '')
  const [occupancyBelow, setOccupancyBelow] = useState(
    permilleField(initial?.occupancyBelowPermille ?? null),
  )
  const [occupancyAbove, setOccupancyAbove] = useState(
    permilleField(initial?.occupancyAbovePermille ?? null),
  )
  const [leadWithin, setLeadWithin] = useState(numberField(initial?.leadTimeWithinDays ?? null))
  const [leadBeyond, setLeadBeyond] = useState(numberField(initial?.leadTimeBeyondDays ?? null))

  function submit(event: FormEvent): void {
    event.preventDefault()
    // Percentage in, promille eruit: alles onder water rekent in promille zodat 7,5%
    // exact past (§17.6).
    const adjustPermille = Math.round(Number(percent) * 10)

    onSave({
      kind,
      label: label.trim(),
      // Een nieuwe regel begint als concept; een bestaande houdt zijn stand.
      // Aanzetten is een aparte handeling — de hele bedoeling van "simulatie vóór
      // activatie" (ADR-118).
      status: initial?.status ?? 'draft',
      adjustPermille,
      priority: Number(priority) || 0,
      startsOn: kind === 'season' ? startsOn : null,
      endsOn: kind === 'season' ? endsOn : null,
      weekdays: kind === 'weekday' ? weekdays : null,
      eventId: kind === 'event' && eventId !== '' ? eventId : null,
      occupancyBelowPermille: kind === 'occupancy' ? toPermille(occupancyBelow) : null,
      occupancyAbovePermille: kind === 'occupancy' ? toPermille(occupancyAbove) : null,
      leadTimeWithinDays: kind === 'lead_time' ? toWholeNumber(leadWithin) : null,
      leadTimeBeyondDays: kind === 'lead_time' ? toWholeNumber(leadBeyond) : null,
    })
  }

  return (
    <form className="rule-form" onSubmit={submit}>
      <h3 className="page__sectionTitle">{t('dashboard.pricing.rules.newTitle')}</h3>

      <div className="rule-form__row">
        <label className="field">
          <span className="field__label">{t('dashboard.pricing.field.kind')}</span>
          <select
            className="field__input"
            disabled={busy}
            onChange={(event) => {
              const next = RULE_KINDS.find((candidate) => candidate === event.target.value)
              if (next !== undefined) {
                setKind(next)
              }
            }}
            value={kind}
          >
            {RULE_KINDS.map((candidate) => (
              <option key={candidate} value={candidate}>
                {t(`dashboard.pricing.kind.${candidate}`)}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span className="field__label">{t('dashboard.pricing.field.label')}</span>
          <input
            className="field__input"
            disabled={busy}
            maxLength={100}
            onChange={(event) => {
              setLabel(event.target.value)
            }}
            type="text"
            value={label}
          />
        </label>

        <label className="field">
          <span className="field__label">{t('dashboard.pricing.field.percent')}</span>
          <input
            className="field__input field__input--narrow"
            disabled={busy}
            max={500}
            min={-90}
            onChange={(event) => {
              setPercent(event.target.value)
            }}
            step="0.1"
            type="number"
            value={percent}
          />
        </label>

        {/* Binnen dezelfde soort wint de hoogste prioriteit (ADR-111). Dat stond in
            het ontwerp en was nergens in te stellen: elke regel kreeg nul. */}
        <label className="field">
          <span className="field__label">{t('dashboard.pricing.field.priority')}</span>
          <input
            className="field__input field__input--narrow"
            disabled={busy}
            max={1000}
            min={-1000}
            onChange={(event) => {
              setPriority(event.target.value)
            }}
            type="number"
            value={priority}
          />
          <span className="field__hint">{t('dashboard.pricing.field.priorityHint')}</span>
        </label>
      </div>

      {kind === 'season' && (
        <div className="rule-form__row">
          <label className="field">
            <span className="field__label">{t('dashboard.pricing.field.startsOn')}</span>
            <input
              className="field__input"
              disabled={busy}
              onChange={(event) => {
                setStartsOn(event.target.value)
              }}
              required
              type="date"
              value={startsOn}
            />
          </label>
          <label className="field">
            <span className="field__label">{t('dashboard.pricing.field.endsOn')}</span>
            <input
              className="field__input"
              disabled={busy}
              onChange={(event) => {
                setEndsOn(event.target.value)
              }}
              required
              type="date"
              value={endsOn}
            />
          </label>
          {/* Beide dagen tellen mee — anders dan bij een reservering. Dat verschil
              staat er, want het is de vergissing die iedereen één keer maakt. */}
          <p className="field__hint">{t('dashboard.pricing.field.inclusive')}</p>
        </div>
      )}

      {kind === 'weekday' && (
        <fieldset className="rule-form__days">
          <legend className="field__label">{t('dashboard.pricing.field.weekdays')}</legend>
          {[1, 2, 3, 4, 5, 6, 7].map((day) => (
            <label className="rule-form__day" key={day}>
              <input
                checked={weekdays.includes(day)}
                disabled={busy}
                onChange={(event) => {
                  setWeekdays((current) =>
                    event.target.checked
                      ? [...current, day].sort((a, b) => a - b)
                      : current.filter((entry) => entry !== day),
                  )
                }}
                type="checkbox"
              />
              <span>{t(`dashboard.pricing.weekday.${String(day)}`)}</span>
            </label>
          ))}
        </fieldset>
      )}

      {kind === 'event' && (
        <label className="field">
          <span className="field__label">{t('dashboard.pricing.field.event')}</span>
          <select
            className="field__input"
            disabled={busy}
            onChange={(event) => {
              setEventId(event.target.value)
            }}
            required
            value={eventId}
          >
            <option value="">{t('dashboard.pricing.field.eventNone')}</option>
            {events.map((event) => (
              <option key={event.id} value={event.id}>
                {event.name} ({event.startsOn} – {event.endsOn})
              </option>
            ))}
          </select>
        </label>
      )}

      {kind === 'occupancy' && (
        <div className="rule-form__row">
          <label className="field">
            <span className="field__label">{t('dashboard.pricing.field.occupancyAbove')}</span>
            <input
              className="field__input field__input--narrow"
              disabled={busy}
              max={100}
              min={0}
              onChange={(event) => {
                setOccupancyAbove(event.target.value)
              }}
              type="number"
              value={occupancyAbove}
            />
          </label>
          <label className="field">
            <span className="field__label">{t('dashboard.pricing.field.occupancyBelow')}</span>
            <input
              className="field__input field__input--narrow"
              disabled={busy}
              max={100}
              min={0}
              onChange={(event) => {
                setOccupancyBelow(event.target.value)
              }}
              type="number"
              value={occupancyBelow}
            />
          </label>
          {/* Allebei mogen leeg zijn zolang er één is ingevuld; leeg betekent geen
              grens en niet nul. Alleen een bovengrens verhoogt bij een volle maand,
              alleen een ondergrens verlaagt bij een lege. */}
          <p className="field__hint">{t('dashboard.pricing.field.occupancyHint')}</p>
        </div>
      )}

      {kind === 'lead_time' && (
        <div className="rule-form__row">
          <label className="field">
            <span className="field__label">{t('dashboard.pricing.field.leadBeyond')}</span>
            <input
              className="field__input field__input--narrow"
              disabled={busy}
              max={730}
              min={0}
              onChange={(event) => {
                setLeadBeyond(event.target.value)
              }}
              type="number"
              value={leadBeyond}
            />
          </label>
          <label className="field">
            <span className="field__label">{t('dashboard.pricing.field.leadWithin')}</span>
            <input
              className="field__input field__input--narrow"
              disabled={busy}
              max={730}
              min={0}
              onChange={(event) => {
                setLeadWithin(event.target.value)
              }}
              type="number"
              value={leadWithin}
            />
          </label>
          <p className="field__hint">{t('dashboard.pricing.field.leadHint')}</p>
        </div>
      )}

      <div className="rule-form__actions">
        <button className="button button--primary" disabled={busy} type="submit">
          {initial === null
            ? t('dashboard.pricing.rules.saveDraft')
            : t('dashboard.pricing.rules.saveChanges')}
        </button>
        <button className="button button--quiet" disabled={busy} onClick={onCancel} type="button">
          {t('dashboard.pricing.rules.cancel')}
        </button>
      </div>
    </form>
  )
}

function EventList({
  events,
  busy,
  onCreate,
  onRetire,
}: {
  readonly events: readonly PricingEvent[]
  readonly busy: boolean
  readonly onCreate: (input: {
    name: string
    startsOn: string
    endsOn: string
    sourceUrl: string
  }) => void
  readonly onRetire: (eventId: string) => void
}): ReactElement {
  const t = useTranslate()
  const [name, setName] = useState('')
  const [startsOn, setStartsOn] = useState('')
  const [endsOn, setEndsOn] = useState('')

  return (
    <>
      <h2 className="page__sectionTitle">{t('dashboard.pricing.events.title')}</h2>
      <p className="prose">{t('dashboard.pricing.events.intro')}</p>

      {events.length > 0 && (
        <ul className="event-list">
          {events.map((event) => (
            <li className="event-row" key={event.id}>
              <span className="event-row__name">{event.name}</span>
              <span className="event-row__period">
                {event.startsOn} – {event.endsOn}
              </span>
              {event.fromArea ? (
                <span className="event-row__source">{t('dashboard.pricing.events.platform')}</span>
              ) : (
                <button
                  className="button button--quiet"
                  disabled={busy}
                  onClick={() => {
                    onRetire(event.id)
                  }}
                  type="button"
                >
                  {t('dashboard.pricing.events.remove')}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <form
        className="event-form"
        onSubmit={(submitEvent) => {
          submitEvent.preventDefault()
          onCreate({ name: name.trim(), startsOn, endsOn, sourceUrl: '' })
          setName('')
        }}
      >
        <label className="field">
          <span className="field__label">{t('dashboard.pricing.field.eventName')}</span>
          <input
            className="field__input"
            disabled={busy}
            maxLength={120}
            onChange={(event) => {
              setName(event.target.value)
            }}
            required
            type="text"
            value={name}
          />
        </label>
        <label className="field">
          <span className="field__label">{t('dashboard.pricing.field.startsOn')}</span>
          <input
            className="field__input"
            disabled={busy}
            onChange={(event) => {
              setStartsOn(event.target.value)
            }}
            required
            type="date"
            value={startsOn}
          />
        </label>
        <label className="field">
          <span className="field__label">{t('dashboard.pricing.field.endsOn')}</span>
          <input
            className="field__input"
            disabled={busy}
            onChange={(event) => {
              setEndsOn(event.target.value)
            }}
            required
            type="date"
            value={endsOn}
          />
        </label>
        <button className="button" disabled={busy} type="submit">
          {t('dashboard.pricing.events.add')}
        </button>
      </form>
    </>
  )
}

function shift(date: string, days: number): string {
  const moment = new Date(`${date}T00:00:00Z`)
  moment.setUTCDate(moment.getUTCDate() + days)
  return moment.toISOString().slice(0, 10)
}

/**
 * Promille naar een percentage voor in een invoerveld; leeg wanneer er geen grens is.
 *
 * Leeg en nul zijn hier niet hetzelfde. Een bezettingsregel met een ondergrens van
 * nul geldt altijd; een regel zonder ondergrens geldt alleen onder de bovengrens.
 */
function permilleField(value: number | null): string {
  return value === null ? '' : String(value / 10)
}

function numberField(value: number | null): string {
  return value === null ? '' : String(value)
}

/** Een leeg veld is geen grens; zie `permilleField`. */
function toPermille(value: string): number | null {
  if (value.trim() === '') {
    return null
  }
  const percent = Number(value)
  return Number.isFinite(percent) ? Math.round(percent * 10) : null
}

function toWholeNumber(value: string): number | null {
  if (value.trim() === '') {
    return null
  }
  const days = Number(value)
  return Number.isInteger(days) ? days : null
}
